import React, { useMemo, useRef } from "react";
import { clampVisibleRange } from "./displayDensity";

type ChartNavigatorProps = {
  pointCount: number;
  visibleRange: [number, number];
  values: Array<number | null>;
  onChange: (range: [number, number]) => void;
};

type DragKind = "move" | "start" | "end";

function sparklinePath(values: Array<number | null>, width: number, height: number): string {
  const numbers = values.filter((value): value is number => Number.isFinite(value));
  if (numbers.length === 0 || width <= 0 || height <= 0) return "";
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  const range = Math.max(max - min, Number.EPSILON);
  const last = Math.max(values.length - 1, 1);
  const points = values.flatMap((value, index) => {
    if (!Number.isFinite(value)) return [];
    const x = (index / last) * width;
    const y = height - ((Number(value) - min) / range) * height;
    return [`${x.toFixed(2)},${y.toFixed(2)}`];
  });
  return points.length ? `M ${points.join(" L ")}` : "";
}

export default function ChartNavigator({
  pointCount,
  visibleRange,
  values,
  onChange,
}: ChartNavigatorProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ kind: DragKind; originX: number; start: number; end: number } | null>(null);
  const lastIndex = Math.max(0, pointCount - 1);
  const [start, end] = visibleRange;
  const leftPct = lastIndex === 0 ? 0 : (start / lastIndex) * 100;
  const widthPct = lastIndex === 0 ? 100 : ((end - start) / lastIndex) * 100;
  const path = useMemo(() => sparklinePath(values, 100, 28), [values]);

  const rangeFromClientX = (clientX: number, kind: DragKind, origin: { start: number; end: number; originX: number }) => {
    const track = trackRef.current;
    if (!track) return origin;
    const bounds = track.getBoundingClientRect();
    if (bounds.width <= 0) return origin;
    const indexAt = (x: number) => Math.round(((x - bounds.left) / bounds.width) * lastIndex);
    if (kind === "start") return { start: indexAt(clientX), end: origin.end };
    if (kind === "end") return { start: origin.start, end: indexAt(clientX) };
    const delta = indexAt(clientX) - indexAt(origin.originX);
    return { start: origin.start + delta, end: origin.end + delta };
  };

  const commit = (kind: DragKind, clientX: number, origin: { start: number; end: number; originX: number }) => {
    const next = rangeFromClientX(clientX, kind, origin);
    onChange(clampVisibleRange(next.start, next.end, lastIndex));
  };

  const beginDrag = (kind: DragKind, event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const origin = { kind, originX: event.clientX, start, end };
    dragRef.current = origin;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  return (
    <div
      className="unified-chart__navigator"
      data-testid="chart-navigator"
      data-visible-start={start}
      data-visible-end={end}
      data-source-point-count={pointCount}
      aria-label="Visible chart range"
    >
      <div
        ref={trackRef}
        className="unified-chart__navigator-track"
        data-testid="chart-navigator-track"
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
          const center = Math.round(ratio * lastIndex);
          const span = end - start;
          onChange(clampVisibleRange(center - Math.floor(span / 2), center - Math.floor(span / 2) + span, lastIndex));
        }}
      >
        <svg className="unified-chart__navigator-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
          {path ? <path d={path} /> : null}
        </svg>
        <div
          className="unified-chart__navigator-window"
          data-testid="chart-navigator-window"
          style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 1.5)}%` }}
          onPointerDown={(event) => beginDrag("move", event)}
          onPointerMove={(event) => {
            if (!dragRef.current || dragRef.current.kind !== "move") return;
            commit("move", event.clientX, dragRef.current);
          }}
          onPointerUp={() => {
            dragRef.current = null;
          }}
        >
          <button
            type="button"
            className="unified-chart__navigator-handle"
            aria-label="Range start"
            data-testid="chart-navigator-start"
            onPointerDown={(event) => beginDrag("start", event)}
            onPointerMove={(event) => {
              if (!dragRef.current || dragRef.current.kind !== "start") return;
              commit("start", event.clientX, dragRef.current);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
          />
          <button
            type="button"
            className="unified-chart__navigator-handle unified-chart__navigator-handle--end"
            aria-label="Range end"
            data-testid="chart-navigator-end"
            onPointerDown={(event) => beginDrag("end", event)}
            onPointerMove={(event) => {
              if (!dragRef.current || dragRef.current.kind !== "end") return;
              commit("end", event.clientX, dragRef.current);
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
          />
        </div>
      </div>
    </div>
  );
}
